import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from 'src/state/auth/auth';
import { get, every } from 'lodash';
import type { OrgUnitActions, Permissions } from 'src/graphql/_generated';

type OrgUnitPermissionKey = keyof Omit<OrgUnitActions, '__typename'>;
type ProfilePermissionKey = keyof Omit<Permissions, '__typename'>;

interface AccessGuardProps {
  children: React.ReactNode;
  activeOrgUnitRecordType?: 'O' | 'W';
  requiredPermissions: {
    orgUnit?: OrgUnitPermissionKey[];
    profile?: ProfilePermissionKey[];
  };
}

export const AccessGuard: React.FC<AccessGuardProps> = ({
  children,
  requiredPermissions = {},
  activeOrgUnitRecordType = 'W',
}) => {
  const { id } = useParams<{ id?: string }>();
  const { profile, flatOrganisations } = useAuth();

  const getOrgUnitPermission = (permission: string): boolean => {
    if (id) {
      // Fall back to profile's active org/workspace permissions (light nav query doesn't include actionsOnThisOrgUnit)
      if (id === profile?.activeOrganisation?.id) {
        return get(profile, `activeOrganisation.actionsOnThisOrganisation.${permission}`, false);
      }
      if (id === profile?.activeWorkspace?.id) {
        return get(profile, `activeWorkspace.actionsOnThisWorkspace.${permission}`, false);
      }

      // Org exists in user's list but no permissions loaded - allow access, page handles its own checks
      const targetOrgUnit = flatOrganisations.find((el) => el.id === id);
      return !!targetOrgUnit;
    }

    if (activeOrgUnitRecordType === 'W') {
      const result = get(profile, `activeWorkspace.actionsOnThisWorkspace.${permission}`, false);

      return result;
    }

    const result = get(profile, `activeOrganisation.actionsOnThisOrganisation.${permission}`, false);
    return result;
  };

  const orgUnitPermissionsPassed = requiredPermissions.orgUnit
    ? every(requiredPermissions.orgUnit, (el) => getOrgUnitPermission(el))
    : true;

  const profilePermissionsPassed = requiredPermissions.profile
    ? every(requiredPermissions.profile, (el) => {
        const result = get(profile, `permissions.${el}`);

        return result;
      })
    : true;

  const hasAccess = orgUnitPermissionsPassed && profilePermissionsPassed;

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
